import React from 'react';
import ToastSuccess from 'containers/ParticipantsPage/components/ToastSuccess';

/**
 * Handles the response from adding people to a study, showing appropriate toasts
 * based on whether the operation was synchronous or asynchronous.
 *
 * Supports two response formats:
 * 1. Standard format: { async, message, count } - POST /studies/{id}/people, share-study-people
 * 2. Create study format: study object + { people_added_async, people_added_message, people_added_count }
 *
 * @param {Object} params
 * @param {Object} params.response - API response
 * @param {boolean} [params.response.async] - True if async (standard format)
 * @param {boolean} [params.response.people_added_async] - True if async (create study format)
 * @param {string} [params.response.message] - Message (standard format)
 * @param {string} [params.response.people_added_message] - Message (create study format)
 * @param {number} [params.response.count] - Count (standard format, sync only)
 * @param {number} [params.response.people_added_count] - Count (create study format)
 * @param {number} params.studyId - ID of the study
 * @param {string} [params.studyName] - Optional name of the study for the success toast
 * @param {number} [params.newCount] - Override count for success toast (e.g., from state)
 * @param {Function} params.addToast - Toast function from useToasts() or toastManager.add
 * @param {Object} [params.toastOptions] - Optional toast options to override defaults
 * @returns {boolean} True if async operation, false if sync
 */
export function handleAddPeopleResponse({ response, studyId, studyName, newCount, addToast, toastOptions = {} }) {
    // Normalize response format - handle both standard and create-study formats
    const isAsync = response.async || response.people_added_async;
    const message = response.message || response.people_added_message;
    const count = response.count != null ? response.count : response.people_added_count;

    if (isAsync) {
        // Async operation - show backend message
        addToast(message, {
            appearance: 'info',
            autoDismiss: true,
            ...toastOptions.async
        });
        return true;
    }

    // Sync operation - show success toast immediately
    const finalCount = newCount !== undefined ? newCount : count;
    addToast(<ToastSuccess studyId={studyId} studyName={studyName} newCount={finalCount} />, {
        appearance: 'success',
        autoDismiss: true,
        big: true,
        ...toastOptions.success
    });
    return false;
}
